import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  LinearProgress,
  Breadcrumbs,
  Link as MLink,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Autocomplete,
  Snackbar,
  Alert,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { caseService, Case } from '../services/caseService';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { projectAPI, caseAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config/environment';
import CaseEstimation from '../components/CaseEstimation';
import ClientEstimation from '../components/ClientEstimation';
import VendorReassignDialog from '../components/VendorReassignDialog';
import VendorAssignmentHistory from '../components/VendorAssignmentHistory';
import { firmService } from '../services/firmService';
import { notificationService } from '../services/notificationService';
import { formatDate, formatDateTime, formatTime, formatUnixTime, formatUnixTimestamp, formatDateLong, unixToNYInputs, nyInputsToUnix } from '../utils/date';

// CSS animations for active timers
const FIELD_REVISION_SOURCE_LABELS: Record<string, string> = {
  update: 'Case update',
  update_and_comment: 'Update with comment',
  reopen: 'Reopen',
  assign_vendor: 'Assign vendor',
  vendor_assign_creation: 'Vendor assign (on create)',
  reassign_vendor: 'Reassign vendor',
  upload_mrr_delivery: 'Upload (MRR delivery)',
  upload_case_delivery: 'Upload (case delivery)',
  vendor_mrr_sync: 'Vendor MRR sync to client',
  auto_close: 'Auto-close (scheduled)',
};

const timerStyles = `
  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.5; }
    100% { opacity: 1; }
  }
  
  @keyframes blink {
    0% { opacity: 1; }
    50% { opacity: 0.3; }
    100% { opacity: 1; }
  }
`;

const CaseView: React.FC = () => {
  const { firmId, caseId } = useParams<{ firmId: string; caseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isPmpAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'pmp_admin');
  };

  const isSiteAdmin = () => {
    if (!user?.roles) return false;
    return user.roles.some(role => role.slug === 'site_admin');
  };

  const isPmpAdminOrSiteAdmin = () => {
    return isPmpAdmin() || isSiteAdmin();
  };

  const isSuperAdmin = isPmpAdminOrSiteAdmin();
  // Helper function to check if user has any client role
  const hasClientRole = () => {
    if (!user?.roles) return false;
    const clientRoleSlugs = ['client_admin', 'client_user', 'client_finance_user'];
    return user.roles.some(role => clientRoleSlugs.includes(role.slug));
  };

  // Helper function to check if user is vendor
  const isVendorUser = () => {
    if (!user?.roles) return false;
    const vendorRoleSlugs = ['vendor_staff', 'vendor_admin'];
    return user.roles.some(role => vendorRoleSlugs.includes(role.slug));
  };

  // Helper function to check if user is client firm user
  const isClientFirmUser = () => {
    if (!user?.roles) return false;
    const clientFirmRoleSlugs = ['firm_admin', 'firm_main', 'firm_staff'];
    return user.roles.some(role => clientFirmRoleSlugs.includes(role.slug));
  };
  const [data, setData] = useState<Case | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [firmName, setFirmName] = useState('');
  const [firm, setFirm] = useState<any>(null);

  // Theme helper function based on firm type
  const getThemeColors = () => {
    if (firm?.type === 'vendor') {
      return {
        primary: '#d32f2f', // Dark Red for vendor
        secondary: '#f57c00',
        gradient: 'linear-gradient(135deg, #f57c00 0%, #d32f2f 100%)', // Orange to Red
        light: '#ff6f00',
        dark: '#b71c1c',
      };
    } else if (firm?.type === 'client') {
      return {
        primary: '#1565c0', // Dark Blue for client
        secondary: '#0277bd',
        gradient: 'linear-gradient(135deg, #0277bd 0%, #1565c0 100%)', // Blue gradient
        light: '#0288d1',
        dark: '#0d47a1',
      };
    } else {
      // Default purple theme
      return {
        primary: '#764ba2',
        secondary: '#667eea',
        gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        light: '#8b6fb8',
        dark: '#5a3a7a',
      };
    }
  };

  const themeColors = getThemeColors();

  // Timer state
  const [entries, setEntries] = useState<any[]>([]);
  const [totalSeconds, setTotalSeconds] = useState<number>(0);
  const [deductionSeconds, setDeductionSeconds] = useState<number>(0);
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [runningStartedAtMs, setRunningStartedAtMs] = useState<number | null>(null);
  const [tick, setTick] = useState<number>(0);
  const [justStopped, setJustStopped] = useState<boolean>(false);
  const [editingEntryId, setEditingEntryId] = useState<number | null>(null);
  const [editingIsSession, setEditingIsSession] = useState<boolean>(false);
  const [activeTimers, setActiveTimers] = useState<any[]>([]);

  // Comments state
  const [comments, setComments] = useState<any[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [visibleCommentsCount, setVisibleCommentsCount] = useState<number>(4);

  // Description read more state
  const [descriptionExpanded, setDescriptionExpanded] = useState<boolean>(false);

  // Upload options state
  const [uploadOptions, setUploadOptions] = useState({
    isMrrDelivery: false,
    isCaseDelivery: false,
    deliveryComment: ''
  });

  // Additional uploads toggle for delivered cases
  const [allowAdditionalUploads, setAllowAdditionalUploads] = useState<boolean>(false);
  // Skip vendor reopen checkbox state
  const [skipVendorReopen, setSkipVendorReopen] = useState<boolean>(false);

  // Refresh trigger for useEffect
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  // Field revision history (PMP / Site admin only; API enforces same)
  const [fieldRevisions, setFieldRevisions] = useState<any[]>([]);
  const [fieldRevisionsLoading, setFieldRevisionsLoading] = useState(false);
  const [fieldRevisionsPage, setFieldRevisionsPage] = useState(0);
  const [fieldRevisionsTotal, setFieldRevisionsTotal] = useState(0);
  const [fieldRevisionsPerPage, setFieldRevisionsPerPage] = useState(25);

  // File upload state
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<{ [key: string]: number }>({});
  const [isUploading, setIsUploading] = useState<boolean>(false);

  // File download state
  const [downloadProgress, setDownloadProgress] = useState<number>(0);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [downloadingFileName, setDownloadingFileName] = useState<string>('');

  // Tag editing state
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [newDisplayName, setNewDisplayName] = useState<string>('');

  // SharePoint import state
  const [sharePointDialogOpen, setSharePointDialogOpen] = useState<boolean>(false);
  const [sharePointUrl, setSharePointUrl] = useState<string>('');
  const [sharePointImporting, setSharePointImporting] = useState<boolean>(false);
  const [microsoftConnected, setMicrosoftConnected] = useState<boolean>(false);
  const [microsoftConnecting, setMicrosoftConnecting] = useState<boolean>(false);

  // Toast notification state
  const [toast, setToast] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Form state for update/comment panel
  const [users, setUsers] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [notifyUsers, setNotifyUsers] = useState<number[]>([]);
  const [notifyAll, setNotifyAll] = useState<boolean>(false);
  const [notifySuppress, setNotifySuppress] = useState<boolean>(false);
  const [commentBody, setCommentBody] = useState<string>('');
  const [commentType, setCommentType] = useState<string>('internal');
  const [notificationType, setNotificationType] = useState<string>('case_comment');

  // Case update form fields
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [selectedAssignedUser, setSelectedAssignedUser] = useState<any>(null);
  const [selectedStatus, setSelectedStatus] = useState<any>(null);
  const [selectedPriority, setSelectedPriority] = useState<any>(null);
  const [selectedType, setSelectedType] = useState<any>(null);

  // Timer entry form fields
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [activity, setActivity] = useState<string>('');
  const [refId, setRefId] = useState<string>('');
  const [disposition, setDisposition] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [meta, setMeta] = useState<{
    statuses: Record<string, string>;
    priorities: Record<string, string>;
    types: Record<string, string>;
    activities: any[];
    dispositions: any[];
  }>({ statuses: {}, priorities: {}, types: {}, activities: [], dispositions: [] });
  const [note, setNote] = useState<string>('');
  const [deductionText, setDeductionText] = useState<string>('');
  // Project activities for time-based projects
  const [projectActivities, setProjectActivities] = useState<any[]>([]);
  const [selectedActivityIds, setSelectedActivityIds] = useState<number[]>([]);
  const [projectSettings, setProjectSettings] = useState<any>(null);

  // Vendor reassignment state
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [assignmentHistory, setAssignmentHistory] = useState<any[]>([]);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  
  // File delete confirmation state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{ id: number; name: string } | null>(null);

  // Accordion expansion state
  const [expandedAccordions, setExpandedAccordions] = useState<{
    updateTask: boolean;
    checklist: boolean;
    caseRecords: boolean;
    caseEstimation: boolean;
    clientEstimation: boolean;
  }>({
    updateTask: false,
    checklist: false,
    caseRecords: false,
    caseEstimation: false,
    clientEstimation: false,
  });

  // Load firm name for breadcrumbs
  useEffect(() => {
    const loadFirm = async () => {
      if (!firmId) return;
      try {
        const res = await firmService.getFirmById(parseInt(firmId));
        setFirm(res.data);
        setFirmName(res.data.name);
      } catch {}
    };
    loadFirm();
  }, [firmId]);

  // Microsoft connection status check - DISABLED FOR NOW
  // useEffect(() => {
  //   const checkMicrosoftStatus = async () => {
  //     try {
  //       const res = await caseAPI.getMicrosoftStatus();
  //       setMicrosoftConnected(res.data.data?.connected || false);
  //     } catch (err) {
  //       setMicrosoftConnected(false);
  //     }
  //   };
  //   checkMicrosoftStatus();
  //   
  //   // Also check on dialog open
  //   if (sharePointDialogOpen) {
  //     checkMicrosoftStatus();
  //   }
  // }, [sharePointDialogOpen]);

  // Handle Microsoft OAuth callback - DISABLED FOR NOW
  // useEffect(() => {
  //   const params = new URLSearchParams(window.location.search);
  //   const microsoftAuth = params.get('microsoft_auth');
  //   if (microsoftAuth === 'success') {
  //     setMicrosoftConnected(true);
  //     showToast('Microsoft account connected successfully', 'success');
  //     // Clean URL
  //     window.history.replaceState({}, document.title, window.location.pathname);
  //   } else if (microsoftAuth === 'error') {
  //     const error = params.get('error');
  //     showToast(`Microsoft connection failed: ${error || 'Unknown error'}`, 'error');
  //     window.history.replaceState({}, document.title, window.location.pathname);
  //   }
  // }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const id = parseInt(caseId!, 10);
        // Pass firm_id to verify case belongs to firm
        try {
          const res = await caseService.getCaseById(id, firmId ? parseInt(firmId) : undefined);
          if (cancelled) return; setData(res.data);
        const timer = await caseService.listTimeEntries(id);
        const loadedEntries = timer.data.entries || [];
        if (cancelled) return; setEntries(loadedEntries);
        if (cancelled) return; setTotalSeconds(timer.data.total_seconds || 0);
        // Determine running state from API session
        const runningSession = (timer.data as any).running_session;
        if (runningSession && typeof runningSession.started_at === 'number') {
          if (cancelled) return; setIsRunning(true);
          if (cancelled) return; setRunningStartedAtMs(runningSession.started_at * 1000);
          if (cancelled) return; setJustStopped(false);
        } else {
          if (cancelled) return; setIsRunning(false);
        }
        // Prefer pending session from API, else fall back to non-finalized entry
        const pendingSession = (timer.data as any).pending_session;
        const pending = pendingSession || ((user
          ? loadedEntries.find((e: any) => e.user?.id === user.id && e.stopped_at && e.is_finalized === false)
          : null) || loadedEntries.find((e: any) => e.stopped_at && e.is_finalized === false));
        if (pending) {
          if (cancelled) return; setEditingEntryId(pending.session_id || pending.id);
          if (cancelled) return; setEditingIsSession(!!pending.session_id);
          if (cancelled) return; setSelectedUserId((pending.user?.id) || selectedUserId || (user?.id ?? null));
          const sUnix = typeof pending.started_at === 'number' ? pending.started_at : undefined;
          const eUnix = typeof pending.stopped_at === 'number' ? pending.stopped_at : undefined;
          const sNY = sUnix ? unixToNYInputs(sUnix) : null;
          const eNY = eUnix ? unixToNYInputs(eUnix) : null;
          if (cancelled) return; setStartDate(sNY ? sNY.date : '');
          if (cancelled) return; setStartTime(sNY ? sNY.time : '');
          if (cancelled) return; setEndDate(eNY ? eNY.date : '');
          if (cancelled) return; setEndTime(eNY ? eNY.time : '');
          // clear optional fields; they will be provided on Save
          if (cancelled) return; setActivity('');
          if (cancelled) return; setRefId('');
          if (cancelled) return; setDisposition('');
          if (cancelled) return; setNote('');
          if (cancelled) return; setDeductionText('');
          if (cancelled) return; setSelectedActivityIds([]);
          if (cancelled) return; setJustStopped(true);
        }
        try {
          const usersRes = await caseAPI.getCommentNotifyUsers(id);
          if (cancelled) return; setUsers(Array.isArray(usersRes.data.data) ? usersRes.data.data : []);
        } catch (_e) {
          if (cancelled) return; setUsers([]);
        }
        
        // Load projects for this case's firm only
        try {
          const firmIdToUse = (res?.data?.project?.firm_id) || (firmId ? Number(firmId) : undefined);
          let projectsData: any[] = [];
          if (firmIdToUse) {
            const projectsRes = await projectAPI.getAll({ firm_id: firmIdToUse });
            projectsData = (projectsRes?.data?.data?.data) || (projectsRes?.data?.data) || [];
            if (!Array.isArray(projectsData)) projectsData = [];
          }
          if (cancelled) return; setProjects(projectsData);
          // Auto-select current case project if present
          if (projectsData.length > 0 && res?.data?.project_id && !selectedProject) {
            const match = projectsData.find((p: any) => Number(p.id) === Number(res.data.project_id));
            if (match) setSelectedProject(match);
          }
        } catch (_e) {
          if (cancelled) return; setProjects([]);
        }
        
        try {
          const metaRes = await caseService.getMeta();
          if (cancelled) return; setMeta((metaRes && (metaRes as any).data) || { statuses: {}, priorities: {}, types: {}, activities: [], dispositions: [] });
        } catch (_e) {
          if (cancelled) return; setMeta({ statuses: {}, priorities: {}, types: {}, activities: [], dispositions: [] });
        }
        
        // Load project settings and activities if project is time-based
        if (res?.data?.project_id) {
          try {
            const settingsRes = await projectAPI.getSettings(res.data.project_id);
            const settings = settingsRes.data.data;
            if (cancelled) return; setProjectSettings(settings);
            
            // Check if project is time-based (estimate_type is 'hours' or 'both')
            if (settings && (settings.estimate_type === 'hours' || settings.estimate_type === 'both')) {
              const activitiesRes = await projectAPI.getActivities(res.data.project_id);
              if (cancelled) return; setProjectActivities((activitiesRes.data.data || []).filter((a: any) => a.is_active && a.is_billable));
            } else {
              if (cancelled) return; setProjectActivities([]);
            }
          } catch (_e) {
            if (cancelled) return; setProjectActivities([]);
            if (cancelled) return; setProjectSettings(null);
          }
        }
        try {
          const activeTimersRes = await caseService.getActiveTimers(id);
          if (cancelled) return; setActiveTimers(activeTimersRes.data.active_timers || []);
        } catch (_e) {
          if (cancelled) return; setActiveTimers([]);
        }
        try {
          const commentsRes = await caseService.getComments(id);
          if (cancelled) return; setComments(Array.isArray(commentsRes.data) ? commentsRes.data : []);
        } catch (_e) {
          if (cancelled) return; setComments([]);
        }
        // Load case files (record manager)
        try {
            const filesRes = await caseService.listFiles(id);
          if (cancelled) return; setData((p: any) => ({ 
            ...(p || {}), 
            fileGroups: filesRes.data.groups, 
            tagDisplayNames: filesRes.data.tag_display_names || {},
            activeTag: Object.keys(filesRes.data.groups)[0] || null, 
            selectedFileIds: [], 
            selectedAll: false 
          }));
        } catch (_e) {
          // ignore
        }
        } catch (error: any) {
          // Handle case access errors
          if (error.message && error.message.includes('does not belong to the specified firm')) {
            showToast('This case does not belong to the selected firm. Redirecting to case list...', 'error');
            setTimeout(() => {
              if (firmId) {
                navigate(`/firms/${firmId}/cases`);
              } else {
                navigate('/cases');
              }
            }, 2000);
            return;
          }
          // Re-throw other errors
          throw error;
        }
      } catch (error: any) {
        // Handle any other errors during load
        showToast(error.message || 'Failed to load case', 'error');
        if (firmId) {
          navigate(`/firms/${firmId}/cases`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [caseId, user?.id, refreshTrigger]);

  // Mark notification as read when comment is viewed via URL hash
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#comment-')) {
      const commentId = parseInt(hash.replace('#comment-', ''), 10);
      if (commentId && !isNaN(commentId)) {
        // Wait a bit for the comment to render, then mark as read
        const timer = setTimeout(() => {
          notificationService.markAsReadByComment(commentId).catch(err => {
            console.error('Failed to mark notification as read:', err);
          });
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [comments]);

  // Mark notification as read when comment scrolls into view
  useEffect(() => {
    if (comments.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elementId = entry.target.id;
            if (elementId && elementId.startsWith('comment-')) {
              const commentId = parseInt(elementId.replace('comment-', ''), 10);
              if (commentId && !isNaN(commentId)) {
                notificationService.markAsReadByComment(commentId).catch(err => {
                  console.error('Failed to mark notification as read:', err);
                });
              }
            }
          }
        });
      },
      { threshold: 0.5 } // Mark as read when 50% visible
    );

    // Observe all comment elements
    comments.forEach((comment) => {
      const element = document.getElementById(`comment-${comment.id}`);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [comments]);

  // Live ticking when running
  useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [isRunning]);

  // Handle "Send notification to all users" checkbox
  useEffect(() => {
    if (notifyAll && users.length > 0) {
      setNotifyUsers(users.map(user => user.id));
    } else if (!notifyAll) {
      setNotifyUsers([]);
    }
  }, [notifyAll, users]);

  // Refresh active timers every second when there are active timers
  useEffect(() => {
    if (activeTimers.length === 0) return;
    const id = setInterval(async () => {
      if (!data) return;
      try {
        const activeTimersRes = await caseService.getActiveTimers(data.id);
        setActiveTimers(activeTimersRes.data.active_timers || []);
      } catch (_e) {
        // Silently fail, don't update state
      }
    }, 1000);
    return () => clearInterval(id);
  }, [activeTimers.length, data]);

  useEffect(() => {
    setFieldRevisions([]);
    setFieldRevisionsPage(0);
    setFieldRevisionsTotal(0);
  }, [data?.id]);

  const refreshActiveTimers = async () => {
    if (!data) return;
    try {
      const activeTimersRes = await caseService.getActiveTimers(data.id);
      setActiveTimers(activeTimersRes.data.active_timers || []);
    } catch (_e) {
      setActiveTimers([]);
    }
  };

  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setToast({
      open: true,
      message,
      severity
    });
  };

  const loadFieldRevisions = async (pageIndex: number, perPage: number = fieldRevisionsPerPage) => {
    if (!data?.id) return;
    setFieldRevisionsLoading(true);
    try {
      const res = await caseService.listFieldRevisions(data.id, {
        per_page: perPage,
        page: pageIndex + 1,
      });
      const p = res.data;
      setFieldRevisions(p.data ?? []);
      setFieldRevisionsTotal(p.total ?? 0);
      setFieldRevisionsPage(pageIndex);
    } catch (error: any) {
      showToast(error.message || 'Failed to load field revision history', 'error');
    } finally {
      setFieldRevisionsLoading(false);
    }
  };

  const handleReassignVendor = async (reassignData: any) => {
    try {
      await caseService.reassignVendor(Number(caseId), reassignData);
      showToast('Vendor reassigned successfully', 'success');
      setRefreshTrigger(prev => prev + 1); // Trigger data reload
    } catch (error: any) {
      throw error; // Re-throw to be caught by dialog
    }
  };

  const loadAssignmentHistory = async () => {
    try {
      const response = await caseService.getVendorAssignmentHistory(Number(caseId));
      setAssignmentHistory(response.data || []);
      setHistoryDialogOpen(true);
    } catch (error: any) {
      showToast(error.message || 'Failed to load assignment history', 'error');
    }
  };

  const handleAssignVendor = async (assignData: any) => {
    try {
      await caseService.assignVendor(Number(caseId), assignData);
      showToast('Vendor assigned successfully', 'success');
      setRefreshTrigger(prev => prev + 1); // Trigger data reload
    } catch (error: any) {
      throw error; // Re-throw to be caught by dialog
    }
  };

  const handleCloseToast = () => {
    setToast(prev => ({ ...prev, open: false }));
  };

  const handleSaveTag = async (tag: string, displayName?: string) => {
    if (!data) {
      setEditingTag(null);
      setNewDisplayName('');
      return;
    }

    try {
      // Call the backend API to update the tag
      const response = await fetch(`${config.apiBaseUrl}/cases/${data.id}/files/tag`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          tag: tag,
          display_name: (displayName ?? '').trim() || null
        })
      });

      const result = await response.json();
      
      if (result.success) {
        // Refresh the file list to get updated tags
        const filesRes = await caseService.listFiles(data.id);
        setData((p: any) => ({ 
          ...p, 
          fileGroups: filesRes.data.groups, 
          tagDisplayNames: filesRes.data.tag_display_names || {},
          activeTag: tag
        }));
        
        setEditingTag(null);
        setNewDisplayName('');
        showToast('Display name updated successfully', 'success');
      } else {
        throw new Error(result.message || 'Failed to update tag');
      }
    } catch (err: any) {
      showToast(err.message || 'Failed to update tag', 'error');
      setEditingTag(null);
      setNewDisplayName('');
    }
  };

  // Inject CSS animations
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = timerStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Reset visible comments when list changes
  useEffect(() => {
    setVisibleCommentsCount((prev) => Math.min(5, comments.length || 0));
  }, [comments.length]);

  if (loading) {
    return <Box display="flex" justifyContent="center" mt={4}><CircularProgress /></Box>;
  }

  if (!data) {
    return <Typography variant="body2" color="text.secondary">Case not found.</Typography>;
  }

  const totalHms = (() => {
    const runningNow = isRunning && runningStartedAtMs ? Math.max(0, Math.floor((Date.now() - runningStartedAtMs) / 1000)) : 0;
    const s = totalSeconds + runningNow;
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h} hrs, ${m} mins, ${sec} secs`;
  })();

  const liveRunHms = (() => {
    if (!isRunning || !runningStartedAtMs) return '';
    const s = Math.max(0, Math.floor((Date.now() - runningStartedAtMs) / 1000));
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    return `${h} hrs, ${m} mins, ${sec} secs`;
  })();

  const caseStatus = String(data.status || '').toLowerCase();
  const isDelivered = caseStatus === 'delivered';
  const isMrrDelivered = caseStatus === 'mrr_delivered';
  const isClosed = caseStatus === 'closed';
  const isCompleted = caseStatus === 'completed';
  /** Delivered/closed/completed need “additional uploads”; mrr_delivered stays open for uploads like other non-terminal states */
  const canUpload =
    (!isDelivered && !isClosed && !isCompleted) || allowAdditionalUploads || isMrrDelivered;
  const showReopenForAdditionalBanner = isDelivered || isMrrDelivered || isClosed || isCompleted;
  const deliveryFlagsApply =
    (!isDelivered && !isClosed && !isCompleted) || allowAdditionalUploads || isMrrDelivered;
  const fileGroups = (data as any).fileGroups || {};
  const groupNames = Object.keys(fileGroups);
  const hadDeliveryEarlier = groupNames.includes('Delivery Records');

  const computeNextAdditionalTagLabel = () => {
    const monthYear = formatDate(new Date(), { month: 'short', year: 'numeric' });
    const base = `Additional ${monthYear}`;
    const existing = groupNames.filter((t) => t.startsWith(base));
    if (existing.length === 0) return base;
    
    let maxCounter = 0;
    for (const tag of existing) {
      // Match "Additional Aug 2025" (counter 0) or "Additional Aug 2025 - 1" (counter 1)
      const m = tag.match(new RegExp(`Additional ${monthYear.replace(/\s+/g, '\\s+')}(?:\\s*-\\s*(\\d+))?$`));
      if (m) {
        const counter = m[1] ? parseInt(m[1], 10) : 0;
        if (!isNaN(counter)) maxCounter = Math.max(maxCounter, counter);
      }
    }
    
    // If we have existing tags but maxCounter is 0, it means we have "Additional Aug 2025" 
    // without a counter, so the next one should be "Additional Aug 2025 - 1"
    if (maxCounter === 0 && existing.length > 0) {
      return `${base} - 1`;
    }
    
    return `${base} - ${maxCounter + 1}`;
  };

  const getUploadTargetLabel = () => {
    if (allowAdditionalUploads || (!isDelivered && !isClosed && !isCompleted && hadDeliveryEarlier)) {
      return computeNextAdditionalTagLabel();
    }
    if (uploadOptions.isMrrDelivery) return 'MRR Delivery Records';
    if (uploadOptions.isCaseDelivery) return 'Delivery Records';
    return 'Initial Records';
  };

  const handleStartTimer = async () => {
    if (!data) return;
    const id = data.id;
    const payload: any = {};
    if (isSuperAdmin && startDate && startTime) {
      payload.started_at = Math.floor(new Date(`${startDate}T${startTime}`).getTime() / 1000);
    }
    try {
      await caseService.startTimer(id, Object.keys(payload).length ? payload : undefined);
      const timer = await caseService.listTimeEntries(id);
      const loadedEntries = timer.data.entries || [];
      setEntries(loadedEntries);
      setTotalSeconds(timer.data.total_seconds || 0);
      const runningSession = (timer.data as any).running_session;
      if (runningSession && typeof runningSession.started_at === 'number') {
        setIsRunning(true);
        setRunningStartedAtMs(runningSession.started_at * 1000);
      } else {
        setIsRunning(false);
        setRunningStartedAtMs(null);
      }
      setJustStopped(false);
      // Refresh active timers
      await refreshActiveTimers();
    } catch (e: any) {
      showToast(e.message || 'Failed to start timer', 'error');
    }
  };

  const handleStopTimer = async () => {
    if (!data) return;
    const id = data.id;
    try {
      const stopResponse = await caseService.stopTimer(id);
      
      // Add a small delay to ensure backend has processed the stop request
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const timer = await caseService.listTimeEntries(id);
      const loadedEntries = timer.data.entries || [];
      setEntries(loadedEntries);
      setTotalSeconds(timer.data.total_seconds || 0);
      setIsRunning(false);
      setRunningStartedAtMs(null);
      setJustStopped(true);
      // populate from pending_session
      const pendingSession = (timer.data as any).pending_session;
      if (pendingSession) {
        setEditingEntryId(pendingSession.session_id);
        setEditingIsSession(true);
        setSelectedUserId(user?.id || null);
        const startedUnix = pendingSession.started_at as number | undefined;
        const stoppedUnix = pendingSession.stopped_at as number | undefined;

        // Race-condition guard: if both timestamps are identical use the stopTimer response
        const hasSameTimestamps = startedUnix && stoppedUnix && startedUnix === stoppedUnix;
        const resolvedStartUnix = (hasSameTimestamps && stopResponse?.data?.started_at) ? stopResponse.data.started_at : startedUnix;
        const resolvedStopUnix  = (hasSameTimestamps && stopResponse?.data?.stopped_at)  ? stopResponse.data.stopped_at
                                : (stoppedUnix || (startedUnix ? Math.floor(Date.now() / 1000) : undefined));

        const sNY = resolvedStartUnix ? unixToNYInputs(resolvedStartUnix) : null;
        const eNY = resolvedStopUnix  ? unixToNYInputs(resolvedStopUnix)  : null;
        setStartDate(sNY ? sNY.date : '');
        setStartTime(sNY ? sNY.time : '');
        setEndDate(eNY ? eNY.date : '');
        setEndTime(eNY ? eNY.time : '');
        setDeductionText('');
        setNote('');
        setActivity('');
        setRefId('');
        setDisposition('');
      }
      // Refresh active timers
      await refreshActiveTimers();
    } catch (e: any) {
      showToast(e.message || 'Failed to stop timer', 'error');
    }
  };

  const handleResumeTimer = async () => {
    if (!data) return;
    try {
      await caseService.resumeTimer(data.id);
      const timer = await caseService.listTimeEntries(data.id);
      const runningSession = (timer.data as any).running_session;
      if (runningSession && typeof runningSession.started_at === 'number') {
        setIsRunning(true);
        setRunningStartedAtMs(runningSession.started_at * 1000);
        setJustStopped(false);
      }
      // Refresh active timers
      await refreshActiveTimers();
    } catch (e: any) {
      showToast(e.message || 'Failed to resume timer', 'error');
    }
  };

  const handleResetTimer = () => {
    (async () => {
      if (!data) return;
      try {
        await caseService.resetTimer(data.id, { session_id: editingEntryId });
        // clear local form/state
        setEditingEntryId(null);
        setStartDate('');
        setStartTime('');
        setEndDate('');
        setEndTime('');
        setActivity('');
        setDisposition('');
        setRefId('');
        setNote('');
        setDeductionText('');
        setJustStopped(false);
        // reload timer state
        const timer = await caseService.listTimeEntries(data.id);
        setEntries(timer.data.entries || []);
        setTotalSeconds(timer.data.total_seconds || 0);
        const runningSession = (timer.data as any).running_session;
        if (runningSession && typeof runningSession.started_at === 'number') {
          setIsRunning(true);
          setRunningStartedAtMs(runningSession.started_at * 1000);
        } else {
          setIsRunning(false);
          setRunningStartedAtMs(null);
        }
      } catch (e: any) {
        showToast(e.message || 'Failed to reset timer', 'error');
      }
    })();
  };

  return (
    <Box>
      {firmId && (
        <Breadcrumbs sx={{ mb: 2 }}>
          <MLink
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate('/firms'); }}
            underline="hover"
          >
            Firms
          </MLink>
          <MLink
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate(`/firms/${firmId}`); }}
            underline="hover"
          >
            {firmName || `Firm #${firmId}`}
          </MLink>
          <MLink
            color="inherit"
            href="#"
            onClick={(e) => { e.preventDefault(); navigate(`/firms/${firmId}/cases`); }}
            underline="hover"
          >
            Cases
          </MLink>
          <Typography color="text.primary">Case #{data.id}</Typography>
        </Breadcrumbs>
      )}

      <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
        <Button variant="outlined" onClick={() => navigate(-1)}>
          Back
        </Button>
        <Typography variant="h4" sx={{ flex: 1 }}>{data.title}</Typography>
        <Button
          variant="outlined"
          onClick={() => navigate(`/firms/${firmId}/cases/${caseId}/edit`)}
        >
          Edit Case
        </Button>
      </Box>
      
      {/* Two-column top layout: Left = Case Info + Settings, Right = Comments */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '3fr 2fr' }, gap: 2, alignItems: 'stretch', mb: 2 }}>
        {/* Left column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* 📋 Case Information */}
          <Paper sx={{ 
            p: 3, 
            mb: 2, 
            background: themeColors.gradient,
            color: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)'
          }}>
            <Typography variant="h5" gutterBottom sx={{ 
              fontWeight: 'bold',
              textShadow: '1px 1px 2px rgba(0,0,0,0.3)',
              mb: 2
            }}>
              📋 Case Information
            </Typography>
            
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
              gap: 2 
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  Project:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {data.project?.name || 'N/A'}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  Status:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {data.status ? data.status.replace('_', ' ').toUpperCase() : 'N/A'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  Priority:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {data.priority ? data.priority.replace('_', ' ').toUpperCase() : 'Normal'}
                </Typography>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  Type:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {data.type ? data.type.replace('_', ' ').toUpperCase() : 'N/A'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
              gap: 2,
              mt: 1
            }}>
              <Box>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  Assigned to:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 2 }}>
                  {(data as any).assigned_to?.name || '—'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mt: 2, pt: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                Description:
              </Typography>
              <Box>
                {(() => {
                  const description = (data as any).description || '—';
                  const maxLength = 200;
                  const shouldTruncate = description.length > maxLength && !descriptionExpanded;
                  const displayText = shouldTruncate 
                    ? description.substring(0, maxLength) + '...' 
                    : description;
                  
                  return (
                    <>
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          fontWeight: 'bold', 
                          mb: shouldTruncate || descriptionExpanded ? 1 : 2,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere'
                        }}
                      >
                        {displayText}
                      </Typography>
                      {description.length > maxLength && (
                        <Button
                          onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                          sx={{
                            color: 'rgba(255,255,255,0.9)',
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            minWidth: 'auto',
                            p: 0,
                            '&:hover': {
                              backgroundColor: 'transparent',
                              textDecoration: 'underline',
                            }
                          }}
                        >
                          {descriptionExpanded ? 'Read less' : 'Read more'}
                        </Button>
                      )}
                    </>
                  );
                })()}
              </Box>
            </Box>
            
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                Submitted by:
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                {data.creator?.name || 'Unknown'} on {formatDateLong(data.created_at)} - {formatTime(data.created_at, { hour: 'numeric', minute: '2-digit', hour12: true })}
              </Typography>
            </Box>

            {/* Vendor Assignment Info - Only for PMP Admin on client cases */}
            {isSuperAdmin && (data as any)?.client_assignment && (data as any).client_assignment.vendor_case_id && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  Assigned to Vendor:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {(data as any).client_assignment.vendorFirm?.name || (data as any).client_assignment.vendor_firm?.name || 'Unknown Vendor'}
                </Typography>
                {((data as any).client_assignment.vendorUser || (data as any).client_assignment.vendor_user) && (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    Vendor User: {(data as any).client_assignment.vendorUser?.name || (data as any).client_assignment.vendor_user?.name || '—'}
                  </Typography>
                )}
                {((data as any).client_assignment.vendorCase || (data as any).client_assignment.vendor_case_id) && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const vendorFirmId = (data as any).client_assignment.vendorFirm?.id || (data as any).client_assignment.vendor_firm_id;
                      const vendorCaseId = (data as any).client_assignment.vendorCase?.id || (data as any).client_assignment.vendor_case_id;
                      if (vendorFirmId && vendorCaseId) {
                        navigate(`/firms/${vendorFirmId}/cases/${vendorCaseId}`);
                      }
                    }}
                    sx={{
                      color: 'rgba(255,255,255,0.9)',
                      borderColor: 'rgba(255,255,255,0.5)',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.8)',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    View Vendor Case
                  </Button>
                )}
              </Box>
            )}

            {/* Vendor Assignment Section - Only show for vendor cases (not client cases) */}
            {(data as any)?.vendor_assignment && (
              <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 0.5 }}>
                  Assigned Vendor:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
                  {(data as any)?.assignedVendor?.name || '—'}
                </Typography>
                {(data as any)?.assignedVendorUser && (
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
                    User: {(data as any)?.assignedVendorUser?.name}
                  </Typography>
                )}
                
                {/* View Client Case button - Only for Site Admin or PMP Admin */}
                {isPmpAdminOrSiteAdmin() && 
                 ((data as any)?.vendor_assignment?.clientCase || (data as any)?.vendor_assignment?.client_case_id) && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const vendorAssignment = (data as any)?.vendor_assignment;
                      const clientCase = vendorAssignment?.clientCase;
                      const clientCaseId = clientCase?.id || vendorAssignment?.client_case_id;
                      // Try multiple ways to get the client firm ID
                      const clientFirmId = clientCase?.project?.firm_id || 
                                          vendorAssignment?.clientFirm?.id || 
                                          vendorAssignment?.client_firm_id;
                      if (clientFirmId && clientCaseId) {
                        navigate(`/firms/${clientFirmId}/cases/${clientCaseId}`);
                      }
                    }}
                    sx={{
                      mt: 1,
                      color: 'rgba(255,255,255,0.9)',
                      borderColor: 'rgba(255,255,255,0.5)',
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.8)',
                        backgroundColor: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    View Client Case
                  </Button>
                )}
                
                {/* Only show reassignment buttons to PMP Admin */}
                {isSuperAdmin && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setReassignDialogOpen(true)}
                      sx={{
                        color: 'white',
                        borderColor: 'rgba(255,255,255,0.5)',
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                    >
                      Reassign Vendor
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={loadAssignmentHistory}
                      sx={{
                        color: 'white',
                        borderColor: 'rgba(255,255,255,0.5)',
                        '&:hover': {
                          borderColor: 'white',
                          backgroundColor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                    >
                      View History
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Paper>

          {/* Case Settings */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Case Settings</Typography>
            <Typography variant="body2">Free Case?: {data.free_case ? 'Yes' : 'No'}</Typography>
          </Paper>

          {isPmpAdmin() && (
            <Accordion
              sx={{ mb: 2 }}
              onChange={(_e, expanded) => {
                if (expanded && data?.id) {
                  loadFieldRevisions(0);
                }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Case Revision History
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
                  (PMP admin only)
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  When case fields change, a row is stored per field: previous value, new value, source, and user.
                </Typography>
                {fieldRevisionsLoading && fieldRevisions.length === 0 ? (
                  <Box display="flex" justifyContent="center" py={2}>
                    <CircularProgress size={28} />
                  </Box>
                ) : fieldRevisions.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No revisions recorded yet.
                  </Typography>
                ) : (
                  <>
                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '18%' }}>When</TableCell>
                          <TableCell sx={{ width: '12%' }}>Field</TableCell>
                          <TableCell sx={{ width: '22%' }}>Old value</TableCell>
                          <TableCell sx={{ width: '22%' }}>New value</TableCell>
                          <TableCell sx={{ width: '16%' }}>Source</TableCell>
                          <TableCell sx={{ width: '10%' }}>User</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fieldRevisions.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                              {row.created_at
                                ? formatDateTime(new Date(row.created_at))
                                : '—'}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                              {row.field_name}
                            </TableCell>
                            <TableCell
                              sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}
                              title={row.old_value ?? ''}
                            >
                              {row.old_value ?? '—'}
                            </TableCell>
                            <TableCell
                              sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}
                              title={row.new_value ?? ''}
                            >
                              {row.new_value ?? '—'}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                              {FIELD_REVISION_SOURCE_LABELS[row.source] || row.source}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                              {row.user?.name ?? (row.source === 'auto_close' ? 'System' : '—')}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={fieldRevisionsTotal}
                      page={fieldRevisionsPage}
                      onPageChange={(_e, newPage) => loadFieldRevisions(newPage)}
                      rowsPerPage={fieldRevisionsPerPage}
                      onRowsPerPageChange={(e) => {
                        const next = parseInt(e.target.value, 10);
                        setFieldRevisionsPerPage(next);
                        loadFieldRevisions(0, next);
                      }}
                      rowsPerPageOptions={[10, 25, 50]}
                    />
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          )}
          
        </Box>

        {/* Right column: Comments (fixed height, scrollable) */}
        <Paper sx={{ p: 3, height: 500, maxHeight: 500, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h6" gutterBottom>Comments</Typography>
          <Box sx={{ flex: 1, minHeight: 0, maxHeight: '100%', overflowY: 'auto' }}>
            {comments.length > 0 ? (
              <Box>
                {comments.slice(0, visibleCommentsCount).map((comment) => (
                  <Box 
                    key={comment.id} 
                    id={`comment-${comment.id}`}
                    sx={{ 
                      mb: 2, 
                      p: 2, 
                      border: '1px solid #e0e0e0', 
                      borderRadius: 1,
                      backgroundColor: comment.type === 'internal' ? '#f8f9fa' : '#ffffff',
                      scrollMarginTop: '20px'
                    }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                        {comment.user?.name || comment.user?.username || 'Unknown User'}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Chip 
                          label={comment.type === 'internal' ? 'Internal' : 'External'} 
                          size="small" 
                          color={comment.type === 'internal' ? 'primary' : 'secondary'}
                        />
                        <Chip 
                          label={comment.notification_type?.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Case Comment'} 
                          size="small" 
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {comment.body}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDateTime(comment.created_at)}
                    </Typography>
                  </Box>
                ))}
                {visibleCommentsCount < comments.length && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                    <Button size="small" variant="outlined" onClick={() => setVisibleCommentsCount((c) => Math.min(c + 4, comments.length))}>
                      Load more
                    </Button>
                  </Box>
                )}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">No comments yet.</Typography>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Timer Info - restored to original location below the top summary layout */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Timer Info</Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>Time logged: {totalHms}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
          {!isRunning && !justStopped && (
            <Button variant="contained" onClick={handleStartTimer}>Start timer</Button>
          )}
          {isRunning && (
            <>
              <Button variant="outlined" onClick={handleStopTimer}>Stop timer</Button>
            </>
          )}
          {!isRunning && justStopped && (
            <>
              <Button variant="contained" onClick={handleResumeTimer}>Resume</Button>
              <Button variant="text" onClick={handleResetTimer}>Reset</Button>
            </>
          )}
        </Box>
        {isRunning && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="primary">Active timer: {liveRunHms} (since {formatTime(new Date(runningStartedAtMs!))})</Typography>
          </Box>
        )}

        {/* Active Timers Display - Inside Timer Info */}
        {activeTimers.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                🕐 Active Timers ({activeTimers.length})
              </Typography>
              <Button 
                size="small"
                variant="text" 
                onClick={refreshActiveTimers}
                sx={{ 
                  color: 'primary.main',
                  fontSize: '0.75rem',
                  minWidth: 'auto',
                  p: 0.5
                }}
              >
                🔄
              </Button>
            </Box>
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', 
              gap: 1.5 
            }}>
              {activeTimers.map((timer) => {
                const duration = Math.floor((Date.now() / 1000) - timer.started_at);
                const hours = Math.floor(duration / 3600);
                const minutes = Math.floor((duration % 3600) / 60);
                const seconds = duration % 60;
                const timeString = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                return (
                  <Box key={timer.session_id} sx={{
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                    borderRadius: 1,
                    p: 1.5,
                    border: '1px solid #e0e0e0',
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: '2px',
                      background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4, #45b7d1)',
                      animation: 'pulse 2s infinite'
                    }} />
                    <Typography variant="caption" sx={{ 
                      fontWeight: 'bold',
                      color: 'text.secondary',
                      fontSize: '0.7rem',
                      display: 'block',
                      mb: 0.5
                    }}>
                      {timer.user?.name || timer.user?.username || 'Unknown User'}
                    </Typography>
                    <Typography variant="h6" sx={{ 
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      color: 'primary.main',
                      mb: 0.5,
                      letterSpacing: '0.05em',
                      fontSize: '1.1rem'
                    }}>
                      {timeString}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.65rem'
                    }}>
                      Started {formatUnixTime(timer.started_at)}
                    </Typography>
                    <Box sx={{
                      position: 'absolute',
                      top: '6px',
                      right: '6px',
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      background: '#ff6b6b',
                      animation: 'blink 1s infinite'
                    }} />
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>Time Entries</AccordionSummary>
          <AccordionDetails>
            {/* Listing table with activity/disposition/ops */}
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Activity</TableCell>
                  <TableCell>Disposition</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Deductions</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Note</TableCell>
                  <TableCell>Ops</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.map((e) => {
                  const durTotal = e.duration_seconds || 0;
                  const durNet = Math.max(0, durTotal - (e.deduction_seconds || 0));
                  const fmt = (sec: number) => {
                    const h = Math.floor(sec / 3600);
                    const m = Math.floor((sec % 3600) / 60);
                    const s = sec % 60;
                    const parts = [] as string[];
                    if (h) parts.push(`${h}h`);
                    if (m) parts.push(`${m}m`);
                    parts.push(`${s}s`);
                    return parts.join(' ');
                  };
                  const activityName = (meta.activities || []).find((a: any) => String(a.id) === String(e.activity))?.name || e.activity || '—';
                  const dispositionName = (meta.dispositions || []).find((d: any) => String(d.id) === String(e.disposition))?.name || e.disposition || '—';
                  const timeRange = `${e.started_at ? formatUnixTime(e.started_at) + ' - ' + formatUnixTime(e.stopped_at || e.started_at) : '—'} (${e.started_at ? formatUnixTimestamp(e.started_at, { month: '2-digit', day: '2-digit', year: 'numeric' }) : ''})`;
                  return (
                    <TableRow key={e.id}>
                      <TableCell>{e.user?.name || e.user?.username || '—'}</TableCell>
                      <TableCell>{activityName}</TableCell>
                      <TableCell>{dispositionName}</TableCell>
                      <TableCell>{fmt(durNet)}</TableCell>
                      <TableCell>{e.deduction_seconds ? fmt(e.deduction_seconds) : '—'}</TableCell>
                      <TableCell>{fmt(durTotal)}</TableCell>
                      <TableCell>{timeRange}</TableCell>
                      <TableCell>{e.notes || '—'}</TableCell>
                      <TableCell>
                        {isSuperAdmin && (
                          <Button size="small" onClick={() => {
                            setEditingEntryId(e.id);
                            setEditingIsSession(false);
                            setSelectedUserId(e.user?.id || null);
                            // Always reset ALL four date/time fields in NY timezone
                            // (matches the table). Unconditional sets prevent stale
                            // state from a prior interaction being silently submitted.
                            const sNY = e.started_at ? unixToNYInputs(e.started_at) : null;
                            const eNY = e.stopped_at  ? unixToNYInputs(e.stopped_at)  : null;
                            setStartDate(sNY ? sNY.date : '');
                            setStartTime(sNY ? sNY.time : '');
                            setEndDate(eNY ? eNY.date : '');
                            setEndTime(eNY ? eNY.time : '');
                            setActivity(String(e.activity || ''));
                            setDisposition(String(e.disposition || ''));
                            setRefId(String(e.ref_id || ''));
                            setNote(String(e.notes || ''));
                            setDeductionText(e.deduction_seconds ? String(e.deduction_seconds) : '');
                            setJustStopped(true);
                          }}>edit</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {entries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">No time entries.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </AccordionDetails>
        </Accordion>


        {editingEntryId && (
          <Paper sx={{ p: 3, mb: 2 }}>
            <Typography variant="h6" gutterBottom>Time Entry</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
              {isSuperAdmin && (
                <Autocomplete
                  options={users}
                  getOptionLabel={(o) => o.name || o.username}
                  value={users.find((u) => u.id === selectedUserId) || null}
                  onChange={(_e, v) => setSelectedUserId(v ? v.id : null)}
                  renderInput={(params) => <TextField {...params} label="User" />}
                  sx={{ minWidth: 220 }}
                />
              )}
              <Autocomplete
                options={meta.activities || []}
                getOptionLabel={(o) => o.name}
                value={(meta.activities || []).find((a: any) => String(a.id) === String(activity)) || null}
                onChange={(_e, v) => setActivity(v ? String(v.id) : '')}
                renderInput={(params) => <TextField {...params} label="Activity *" />}
                sx={{ minWidth: 220 }}
              />
              {/* Billable Activities Multi-select - shown only for time-based projects */}
              {projectSettings && (projectSettings.estimate_type === 'hours' || projectSettings.estimate_type === 'both') && projectActivities.length > 0 && (
                <Autocomplete
                  multiple
                  options={projectActivities}
                  getOptionLabel={(o) => o.name}
                  value={projectActivities.filter((a: any) => selectedActivityIds.includes(a.id))}
                  onChange={(_e, v) => setSelectedActivityIds(v.map((a: any) => a.id))}
                  renderInput={(params) => <TextField {...params} label="Billable Activities" placeholder="Select activities" />}
                  renderTags={(value, getTagProps) =>
                    value.map((option: any, index: number) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.name}
                        size="small"
                      />
                    ))
                  }
                  sx={{ minWidth: 300 }}
                />
              )}
              <TextField label="Ref ID" value={refId} onChange={(e) => setRefId(e.target.value)} sx={{ minWidth: 220 }} />
              <Autocomplete
                options={meta.dispositions || []}
                getOptionLabel={(o) => o.name}
                value={(meta.dispositions || []).find((d: any) => String(d.id) === String(disposition)) || null}
                onChange={(_e, v) => setDisposition(v ? String(v.id) : '')}
                renderInput={(params) => <TextField {...params} label="Dispositions *" />}
                sx={{ minWidth: 220 }}
              />
              <TextField label="Start Date" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={{ minWidth: 180 }} disabled={!isSuperAdmin} />
              <TextField label="Start Time" type="time" inputProps={{ step: 1 }} value={startTime} onChange={(e) => setStartTime(e.target.value)} sx={{ minWidth: 160 }} disabled={!isSuperAdmin} />
              <TextField label="End Date" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} sx={{ minWidth: 180 }} disabled={!isSuperAdmin} />
              <TextField label="End Time" type="time" inputProps={{ step: 1 }} value={endTime} onChange={(e) => setEndTime(e.target.value)} sx={{ minWidth: 160 }} disabled={!isSuperAdmin} />
              <TextField label="Deductions (seconds)" placeholder="e.g., 120" value={deductionText} onChange={(e) => setDeductionText(e.target.value)} sx={{ minWidth: 200 }} disabled={!isSuperAdmin} />
              <TextField label="Note" value={note} onChange={(e) => setNote(e.target.value)} multiline minRows={2} sx={{ flex: '1 1 300px' }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" disabled={!editingEntryId || !activity || !disposition} onClick={async () => {
                if (!data || !editingEntryId) return;
                // Convert NY-timezone form inputs to UTC Unix seconds
                const startUnix = nyInputsToUnix(startDate, startTime);
                const stopUnix  = nyInputsToUnix(endDate, endTime);
                if (startUnix !== undefined && stopUnix !== undefined && startUnix >= stopUnix) {
                  showToast('Start time must be before end time', 'error');
                  return;
                }
                try {
                  if (editingIsSession) {
                    const payload: any = {
                      session_id: editingEntryId,
                      notes: note || undefined,
                      activity: activity || undefined,
                      ref_id: refId || undefined,
                      disposition: disposition || undefined,
                      started_at: startUnix,
                      stopped_at: stopUnix,
                    };
                    if (isSuperAdmin && deductionText) {
                      const sec = parseInt(deductionText.trim(), 10);
                      payload.deduction_seconds = isNaN(sec) || sec < 0 ? 0 : sec;
                    }
                    // Add activity_ids if project is time-based and activities are selected
                    if (projectSettings && (projectSettings.estimate_type === 'hours' || projectSettings.estimate_type === 'both') && selectedActivityIds.length > 0) {
                      payload.activity_ids = selectedActivityIds;
                    }
                    await caseService.finalizeTimer(data.id, payload);
                  } else {
                    // update existing entry
                    const payload: any = {
                      notes: note || undefined,
                      activity: activity || undefined,
                      ref_id: refId || undefined,
                      disposition: disposition || undefined,
                      started_at: startUnix,
                      stopped_at: stopUnix,
                    };
                    if (isSuperAdmin && deductionText) {
                      const sec = parseInt(deductionText.trim(), 10);
                      payload.deduction_seconds = isNaN(sec) || sec < 0 ? 0 : sec;
                    }
                    await caseService.updateTimeEntry(data.id, editingEntryId, payload);
                  }
                  const timer = await caseService.listTimeEntries(data.id);
                  setEntries(timer.data.entries || []);
                  setTotalSeconds(timer.data.total_seconds || 0);
                  setJustStopped(false);
                  setEditingEntryId(null);
                  setEditingIsSession(false);
                } catch (e: any) {
                  showToast(e.message || 'Failed to save time entry', 'error');
                }
              }}>Save</Button>
              {!editingIsSession && (
                <Button variant="outlined" color="error" disabled={!editingEntryId || !isSuperAdmin} onClick={async () => {
                  if (!data || !editingEntryId) return;
                  try {
                    await caseService.deleteTimeEntry(data.id, editingEntryId);
                    const timer = await caseService.listTimeEntries(data.id);
                    setEntries(timer.data.entries || []);
                    setTotalSeconds(timer.data.total_seconds || 0);
                    setJustStopped(false);
                    setEditingEntryId(null);
                  } catch (e: any) {
                    showToast(e.message || 'Failed to delete time entry', 'error');
                  }
                }}>Delete</Button>
              )}
            </Box>
          </Paper>
        )}
      </Paper>

      {/* Update Task and Add New Comment */}
      <Accordion 
        expanded={expandedAccordions.updateTask}
        onChange={() => setExpandedAccordions(prev => ({ ...prev, updateTask: !prev.updateTask }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: themeColors.primary,
            '&:hover': { backgroundColor: themeColors.dark },
            color: 'white',
          }}
        >
          <Typography variant="h6" sx={{ color: 'white' }}>Update Task and Add New Comment</Typography>
        </AccordionSummary>
        <AccordionDetails>
        {/* Case Information Subsection */}
        {!hasClientRole() && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" sx={{ 
              fontWeight: 'bold', 
              mb: 2,
              color: 'text.secondary',
              fontStyle: 'italic'
            }}>
              Task Information [Not visible to client users]
            </Typography>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
              <TextField 
                label="Project" 
                select 
                value={selectedProject?.id || ''} 
                onChange={(e) => {
                  const project = projects.find(p => p.id === e.target.value);
                  setSelectedProject(project || null);
                }}
                fullWidth
              >
                <MenuItem value="">- Select a value -</MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                ))}
                {projects.length === 0 && (
                  <MenuItem value="" disabled>No projects available</MenuItem>
                )}
              </TextField>
              
              <TextField 
                label="Assigned to" 
                select 
                value={selectedAssignedUser?.id || ''} 
                onChange={(e) => {
                  const user = users.find(u => u.id === e.target.value);
                  setSelectedAssignedUser(user || null);
                }}
                fullWidth
              >
                <MenuItem value="">- None -</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.name} ({u.email})</MenuItem>
                ))}
              </TextField>
            </Box>
            
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
              <TextField 
                label="Status" 
                select 
                value={selectedStatus?.value || ''} 
                onChange={(e) => {
                  const status = Object.entries(meta.statuses || {}).find(([k, v]) => k === e.target.value);
                  setSelectedStatus(status ? { value: status[0], label: status[1] } : null);
                }}
                fullWidth
              >
                {Object.entries(meta.statuses || {}).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v}</MenuItem>
                ))}
              </TextField>
              
              <TextField 
                label="Priority" 
                select 
                value={selectedPriority?.value || ''} 
                onChange={(e) => {
                  const priority = Object.entries(meta.priorities || {}).find(([k, v]) => k === e.target.value);
                  setSelectedPriority(priority ? { value: priority[0], label: priority[1] } : null);
                }}
                fullWidth
              >
                {Object.entries(meta.priorities || {}).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v}</MenuItem>
                ))}
              </TextField>
              
              <TextField 
                label="Type" 
                select 
                value={selectedType?.value || ''} 
                onChange={(e) => {
                  const type = Object.entries(meta.types || {}).find(([k, v]) => k === e.target.value);
                  setSelectedType(type ? { value: type[0], label: type[1] } : null);
                }}
                fullWidth
              >
                {Object.entries(meta.types || {}).map(([k, v]) => (
                  <MenuItem key={k} value={k}>{v}</MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>
        )}

        {/* Comment Info Subsection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Comment Info
          </Typography>
          
          <TextField
            label="Comment"
            multiline
            rows={4}
            value={commentBody}
            onChange={(e) => setCommentBody(e.target.value)}
            sx={{ width: '100%', mb: 2 }}
            placeholder="Enter your comment here..."
          />
          
          {!hasClientRole() && (
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <Autocomplete
                options={[
                  { value: 'internal', label: 'Internal' },
                  { value: 'external', label: 'External' }
                ]}
                getOptionLabel={(option) => option.label || ''}
                value={commentType ? { value: commentType, label: commentType === 'internal' ? 'Internal' : 'External' } : null}
                onChange={(e, value) => setCommentType(value?.value || 'internal')}
                renderInput={(params) => <TextField {...params} label="Comment Type" />}
                sx={{ minWidth: 200 }}
              />
              
              <Autocomplete
                options={[
                  { value: 'case_comment', label: 'Case Comment' },
                  { value: 'response_expected', label: 'Response Expected' },
                  { value: 'approval_expected', label: 'Approval Expected' }
                ]}
                getOptionLabel={(option) => option.label || ''}
                value={notificationType ? { value: notificationType, label: notificationType.replace('_', ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) } : null}
                onChange={(e, value) => setNotificationType(value?.value || 'case_comment')}
                renderInput={(params) => <TextField {...params} label="Notification Type" />}
                sx={{ minWidth: 200 }}
              />
            </Box>
          )}
        </Box>

        {/* Notification Subsection */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
            Notification
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={notifyAll}
                  onChange={(e) => {
                    setNotifyAll(e.target.checked);
                    if (e.target.checked) {
                      setNotifySuppress(false);
                    }
                  }}
                  disabled={notifySuppress}
                />
              }
              label="Send notification to all users"
            />
            
            <FormControlLabel
              control={
                <Checkbox
                  checked={notifySuppress}
                  onChange={(e) => {
                    setNotifySuppress(e.target.checked);
                    if (e.target.checked) {
                      setNotifyAll(false);
                      setNotifyUsers([]);
                    }
                  }}
                  disabled={notifyAll}
                />
              }
              label="Do not send notification for this update"
            />
            
            {!notifyAll && !notifySuppress && (
              <Autocomplete
                multiple
                options={users}
                getOptionLabel={(option) => option.name || option.username || ''}
                value={users.filter(user => notifyUsers.includes(user.id))}
                onChange={(e, value) => setNotifyUsers(value.map(user => user.id))}
                renderInput={(params) => <TextField {...params} label="Notify Users" placeholder="Select users to notify" />}
                sx={{ minWidth: 300 }}
              />
            )}
          </Box>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={() => {
            // Reset form
            setCommentBody('');
            setCommentType('internal');
            setNotificationType('case_comment');
            setNotifyUsers([]);
            setSelectedProject(null);
            setSelectedAssignedUser(null);
            setSelectedStatus(null);
            setSelectedPriority(null);
            setSelectedType(null);
          }}>
            Reset
          </Button>
          <Button variant="contained" onClick={async () => {
            try {
              if (!data) return;
              
              const payload: any = {
                comment_body: commentBody,
                notify_all: notifyAll,
                notify_suppress: notifySuppress,
                notify_user_ids: notifyUsers,
              };

              // Add case update fields only for non-client users
              if (!hasClientRole()) {
                if (selectedProject) payload.project_id = selectedProject.id;
                if (selectedAssignedUser) payload.assigned_to = selectedAssignedUser.id;
                if (selectedStatus) payload.status = selectedStatus.value;
                if (selectedPriority) payload.priority = selectedPriority.value;
                if (selectedType) payload.type = selectedType.value;
                if (commentType) payload.comment_type = commentType;
                if (notificationType) payload.notification_type = notificationType;
              }

              await caseService.updateCaseAndAddComment(data.id, payload);
              
              // Refresh case data
              const caseRes = await caseService.getCaseById(data.id);
              setData(caseRes.data);
              
              // Refresh comments
              const commentsRes = await caseService.getComments(data.id);
              setComments(Array.isArray(commentsRes.data) ? commentsRes.data : []);
              
              // Reset form
              setCommentBody('');
              setCommentType('internal');
              setNotificationType('case_comment');
              setNotifyUsers([]);
              setNotifyAll(false);
              setNotifySuppress(false);
              setSelectedProject(null);
              setSelectedAssignedUser(null);
              setSelectedStatus(null);
              setSelectedPriority(null);
              setSelectedType(null);
              
              showToast('Task updated and comment added successfully', 'success');
            } catch (error: any) {
              showToast(error.message || 'Failed to update task and add comment', 'error');
            }
          }}>
            Update Task & Add Comment
          </Button>
        </Box>
        </AccordionDetails>
      </Accordion>

      {/* Checklist Panel - only shown when project has checklist enabled */}
      {projectSettings?.custom_fields?.enable_checklist && (
      <Accordion 
        expanded={expandedAccordions.checklist}
        onChange={() => setExpandedAccordions(prev => ({ ...prev, checklist: !prev.checklist }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: themeColors.primary,
            '&:hover': { backgroundColor: themeColors.dark },
            color: 'white',
          }}
        >
          <Typography variant="h6" sx={{ color: 'white' }}>Checklist Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary">No checklist detail present at project level</Typography>
        </AccordionDetails>
      </Accordion>
      )}

      {/* Case Records (File Manager) */}
      <Accordion 
        expanded={expandedAccordions.caseRecords}
        onChange={() => setExpandedAccordions(prev => ({ ...prev, caseRecords: !prev.caseRecords }))}
        sx={{ mb: 2 }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            backgroundColor: themeColors.primary,
            '&:hover': { backgroundColor: themeColors.dark },
            color: 'white',
          }}
        >
          <Typography variant="h6" sx={{ color: 'white' }}>Case Records</Typography>
        </AccordionSummary>
        <AccordionDetails>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '280px 1fr' }, gap: 2 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Folders</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {Object.keys((data as any).fileGroups || {}).map((tag) => {
                const displayName = (data as any).tagDisplayNames?.[tag] || tag;
                return (
                  <Box key={tag} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button 
                      size="small" 
                      variant={((data as any).activeTag || '') === tag ? 'contained' : 'text'} 
                      onClick={() => setData((p: any) => ({ ...p, activeTag: tag }))} 
                      sx={{ justifyContent: 'flex-start', flex: 1 }}
                    >
                      {editingTag === tag ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                          <TextField
                            size="small"
                            label="Tag Name"
                            value={tag}
                            disabled
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                            sx={{ 
                              '& .MuiInputBase-root': { 
                                backgroundColor: 'white',
                                minHeight: 'auto',
                                fontSize: '0.75rem'
                              }
                            }}
                          />
                          <TextField
                            size="small"
                            label="Display Name (optional)"
                            value={newDisplayName}
                            onChange={(e) => setNewDisplayName(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleSaveTag(tag, newDisplayName);
                              }
                            }}
                            sx={{ 
                              '& .MuiInputBase-root': { 
                                backgroundColor: 'white',
                                minHeight: 'auto',
                                fontSize: '0.75rem'
                              }
                            }}
                          />
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '100%' }}>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {displayName}
                          </Typography>
                          {displayName !== tag && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                              {tag}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Button>
                    
                    {/* Tag edit/save buttons - only for non-client users */}
                    {!hasClientRole() && (
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {editingTag === tag ? (
                          <>
                            <Button
                              size="small"
                              color="success"
                            onClick={() => handleSaveTag(tag, newDisplayName)}
                              sx={{ minWidth: 'auto', p: 0.5 }}
                            >
                              ✓
                            </Button>
                            <Button
                              size="small"
                              color="error"
                              onClick={() => {
                                setEditingTag(null);
                                setNewDisplayName('');
                              }}
                              sx={{ minWidth: 'auto', p: 0.5 }}
                            >
                              ✕
                            </Button>
                          </>
                        ) : (
                          <Button
                            size="small"
                            color="primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingTag(tag);
                              setNewDisplayName((data as any).tagDisplayNames?.[tag] || '');
                            }}
                            sx={{ minWidth: 'auto', p: 0.5 }}
                          >
                            ✎
                          </Button>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
              {Object.keys((data as any).fileGroups || {}).length === 0 && (
                <Typography variant="body2" color="text.secondary">No folders yet.</Typography>
              )}
            </Box>
          </Box>
          <Box>
            {/* Delivery Options - Only visible to non-client users and when not delivered */}
            {!hasClientRole() && !isDelivered && !isClosed && !isCompleted && (
              <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fafafa' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Delivery Options</Typography>
                
                {/* MRR Case Delivery */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={uploadOptions.isMrrDelivery}
                      onChange={(e) => setUploadOptions(prev => ({ ...prev, isMrrDelivery: e.target.checked, isCaseDelivery: false }))}
                    />
                  }
                  label="MRR Case Delivery"
                />
                {uploadOptions.isMrrDelivery && (
                  <Box sx={{ ml: 4, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Do you want to Upload MRR Case Delivery?
                    </Typography>
                    <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'bold' }}>
                      ⚠️ Case status will be changed to: MRR Delivered
                    </Typography>
                  </Box>
                )}

                {/* Case Delivery */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={uploadOptions.isCaseDelivery}
                      onChange={(e) => setUploadOptions(prev => ({ ...prev, isCaseDelivery: e.target.checked, isMrrDelivery: false }))}
                    />
                  }
                  label="Case Delivery"
                />
                {uploadOptions.isCaseDelivery && (
                  <Box sx={{ ml: 4, mb: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Do you want to Deliver Case?
                    </Typography>
                    <Typography variant="body2" color="warning.main" sx={{ fontWeight: 'bold', mb: 1 }}>
                      ⚠️ Case status will be changed to: Delivered
                    </Typography>
                    <TextField
                      size="small"
                      placeholder="Enter delivery comment (optional)"
                      value={uploadOptions.deliveryComment}
                      onChange={(e) => setUploadOptions(prev => ({ ...prev, deliveryComment: e.target.value }))}
                      sx={{ width: '100%', maxWidth: 400 }}
                    />
                  </Box>
                )}

                {/* Upload Location Display */}
                {/* <Typography variant="body2" color="primary" sx={{ mt: 1, fontWeight: 'bold' }}>
                  Files will be uploaded to: {getUploadTargetLabel()} Folder
                </Typography> */}
              </Box>
            )}

            {/* Reopen / enable additional uploads (delivered, MRR delivered, closed, or completed) */}
            {showReopenForAdditionalBanner && (
              <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#fffbe6' }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {isClosed
                    ? 'This case is closed. Uploads are disabled until you reopen for additional uploads.'
                    : isCompleted
                      ? 'This case is completed. Uploads are disabled until you reopen for additional uploads.'
                    : isMrrDelivered
                      ? 'This case is MRR delivered. Enable additional uploads when you need more records or another delivery batch.'
                      : 'This case is delivered. Uploads are disabled by default.'}
                </Typography>
                {/* <Typography variant="body2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                  Files will be uploaded to: {getUploadTargetLabel()} Folder
                </Typography> */}
                {(
                  // All users (including client users) can enable additional uploads
                  true
                ) && (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                      <Button
                        variant={allowAdditionalUploads ? 'contained' : 'outlined'}
                        size="small"
                        onClick={async () => {
                          if (!data) return;
                          if (!allowAdditionalUploads) {
                            try {
                              // Reopen case in backend (sets status to open, adds external comment)
                              await caseService.reopenCase(data.id, { skip_vendor_reopen: skipVendorReopen });
                              const caseRes = await caseService.getCaseById(data.id);
                              setData(caseRes.data);
                              
                              // Refresh file groups to show the new Additional tag
                              const filesRes = await caseService.listFiles(data.id);
                              setData((p: any) => ({ 
                                ...p, 
                                fileGroups: filesRes.data.groups, 
                                activeTag: Object.keys(filesRes.data.groups)[0] || null 
                              }));
                              
                              setAllowAdditionalUploads(true);
                              showToast('Case reopened. You can upload additional files now.', 'success');
                            } catch (e: any) {
                              showToast(e.message || 'Failed to reopen case', 'error');
                            }
                          } else {
                            setAllowAdditionalUploads(false);
                          }
                        }}
                      >
                        {allowAdditionalUploads ? 'Additional Uploads Enabled' : 'Reopen for Additional Uploads'}
                      </Button>
                      <Typography variant="body2" color="text.secondary">
                        When enabled, files will go to the Additional folder.
                      </Typography>
                    </Box>
                    {/* Show checkbox only if case has vendor assignment */}
                    {((data as any)?.vendor_assignment || (data as any)?.assigned_vendor_id) && !allowAdditionalUploads && (
                      <Box sx={{ mt: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={skipVendorReopen}
                              onChange={(e) => setSkipVendorReopen(e.target.checked)}
                              size="small"
                            />
                          }
                          label="Reopen case skip vendor assignment"
                        />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {/* File Selection Area - only visible when a delivery option is selected */}
            {(uploadOptions.isMrrDelivery || uploadOptions.isCaseDelivery) && (
            <Box sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8f9fa' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>File Selection</Typography>
              {/* <Typography variant="body2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                Files will be uploaded to: {getUploadTargetLabel()} Folder
              </Typography> */}
              
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                >
                  Select Files
                  <input
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setSelectedFiles(prev => [...prev, ...files]);
                      // allow selecting the same file again
                      if (e.target) e.target.value = '';
                    }}
                  />
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                >
                  Select ZIP File
                  <input
                    type="file"
                    accept=".zip"
                    hidden
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      setSelectedFiles(prev => [...prev, ...files]);
                      // allow selecting the same file again
                      if (e.target) e.target.value = '';
                    }}
                  />
                </Button>
                {selectedFiles.length > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    onClick={() => setSelectedFiles([])}
                  >
                    Clear All
                  </Button>
                )}
              </Box>

              {/* Selected Files List */}
              {selectedFiles.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Selected Files ({selectedFiles.length}):
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto', border: '1px solid #ddd', borderRadius: 1, p: 1 }}>
                    {selectedFiles.map((file, index) => (
                      <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: index < selectedFiles.length - 1 ? '1px solid #eee' : 'none' }}>
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                        </Typography>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => setSelectedFiles(prev => prev.filter((_, i) => i !== index))}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Final Upload Button */}
              {selectedFiles.length > 0 && canUpload && (
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      if (!data) return;
                      
                      try {
                        setIsUploading(true);
                        setUploadProgress({});
                        const zipFiles = selectedFiles.filter((f: File) => f.type === 'application/zip' || f.name.endsWith('.zip'));
                        const regularFiles = selectedFiles.filter((f: File) => f.type !== 'application/zip' && !f.name.endsWith('.zip'));
                        const options = {
                          isMrrDelivery: deliveryFlagsApply && uploadOptions.isMrrDelivery,
                          isCaseDelivery: deliveryFlagsApply && uploadOptions.isCaseDelivery,
                          deliveryComment: uploadOptions.deliveryComment,
                          isAdditionalUpload: allowAdditionalUploads,
                        };
                        if (regularFiles.length > 0) {
                          await caseService.uploadMultipleFiles(
                            data.id,
                            regularFiles,
                            options,
                            (fileName, progress) => setUploadProgress(prev => ({ ...prev, [fileName]: progress }))
                          );
                        }
                        for (const file of zipFiles) {
                          setUploadProgress(prev => ({ ...prev, [file.name]: 0 }));
                          await caseService.uploadZipFile(
                            data.id,
                            file,
                            options,
                            (progress) => setUploadProgress(prev => ({ ...prev, [file.name]: progress }))
                          );
                        }
                        // Refresh file list
                        const filesRes = await caseService.listFiles(data.id);
                        setData((p: any) => ({ ...p, fileGroups: filesRes.data.groups, activeTag: Object.keys(filesRes.data.groups)[0] || null }));
                        
                        // Clear selected files and reset options
                        setSelectedFiles([]);
                        setUploadOptions({ isMrrDelivery: false, isCaseDelivery: false, deliveryComment: '' });
                        setUploadProgress({});
                        
                        const hasPdfOrZip = selectedFiles.some((file: File) =>
                          file.name.toLowerCase().endsWith('.pdf') ||
                          (file.type && file.type.toLowerCase() === 'application/pdf') ||
                          file.name.toLowerCase().endsWith('.zip') ||
                          (file.type && file.type.toLowerCase() === 'application/zip')
                        );
                        showToast(
                          hasPdfOrZip
                            ? `${selectedFiles.length} files uploaded. We're counting PDF pages and will notify you when done.`
                            : `${selectedFiles.length} files uploaded successfully`,
                          'success'
                        );
                      } catch (err: any) {
                        showToast(err.message || 'Upload failed', 'error');
                      } finally {
                        setIsUploading(false);
                      }
                    }}
                    disabled={selectedFiles.length === 0 || isUploading}
                   >
                     {isUploading ? (
                       <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                         <CircularProgress size={16} />
                         <span>Uploading...</span>
                       </Box>
                     ) : (
                       <>
                         Upload {selectedFiles.length} File{selectedFiles.length !== 1 ? 's' : ''}
                         {deliveryFlagsApply && uploadOptions.isMrrDelivery && ' (MRR Delivery)'}
                         {deliveryFlagsApply && uploadOptions.isCaseDelivery && ' (Case Delivery)'}
                       </>
                     )}
                   </Button>
                </Box>
              )}
            </Box>
            )}

            {!(uploadOptions.isMrrDelivery || uploadOptions.isCaseDelivery) && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1, flexWrap: 'wrap' }}>
              {canUpload ? (
              <Button
                variant="outlined"
                size="small"
                component="label"
                disabled={isUploading}
              >
                {isUploading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <span>Uploading...</span>
                  </Box>
                ) : (
                  'Upload File'
                )}
                <input
                  type="file"
                  hidden
                  onChange={async (e) => {
                    if (!data) return;
                    const f = e.target.files?.[0];
                    if (!f) return;
                    
                    const input = e.target;
                    try {
                      setIsUploading(true);
                      setUploadProgress({ [f.name]: 0 });
                      
                      const formData = new FormData();
                      formData.append('file', f);
                      if (allowAdditionalUploads) {
                        formData.append('is_additional_upload', '1');
                      } else if (uploadOptions.isMrrDelivery) {
                        formData.append('is_mrr_delivery', '1');
                      } else if (uploadOptions.isCaseDelivery) {
                        formData.append('is_case_delivery', '1');
                        if (uploadOptions.deliveryComment) {
                          formData.append('delivery_comment', uploadOptions.deliveryComment);
                        }
                      }
                      
                      await caseService.uploadFile(
                        data.id, 
                        f, 
                        formData,
                        (progress) => setUploadProgress({ [f.name]: progress })
                      );
                      
                      const filesRes = await caseService.listFiles(data.id);
                      setData((p: any) => ({ ...p, fileGroups: filesRes.data.groups, activeTag: Object.keys(filesRes.data.groups)[0] || null }));
                      const isPdf = f.name.toLowerCase().endsWith('.pdf') || (f.type && f.type.toLowerCase() === 'application/pdf');
                      showToast(
                        isPdf ? "File uploaded. We're counting pages and will notify you when done." : 'File uploaded',
                        'success'
                      );
                    } catch (err: any) {
                      showToast(err.message || 'Upload failed', 'error');
                    } finally {
                      setIsUploading(false);
                      setUploadProgress({});
                      if (input) input.value = '';
                    }
                  }}
                />
              </Button>
              ) : null}
              {canUpload ? (
              <Button
                variant="outlined"
                size="small"
                component="label"
                disabled={isUploading}
              >
                {isUploading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <span>Uploading...</span>
                  </Box>
                ) : (
                  'Upload Multiple Files'
                )}
                <input
                  type="file"
                  multiple
                  hidden
                  onChange={async (e) => {
                    if (!data) return;
                    const files = Array.from(e.target.files || []);
                    if (files.length === 0) return;
                    
                    const input = e.target;
                    try {
                      setIsUploading(true);
                      const progress: { [key: string]: number } = {};
                      files.forEach(f => progress[f.name] = 0);
                      setUploadProgress(progress);
                      
                      await caseService.uploadMultipleFiles(
                        data.id, 
                        files, 
                        {
                          isMrrDelivery: uploadOptions.isMrrDelivery,
                          isCaseDelivery: uploadOptions.isCaseDelivery,
                          deliveryComment: uploadOptions.deliveryComment || undefined,
                          isAdditionalUpload: allowAdditionalUploads || isDelivered || isClosed || isCompleted
                        },
                        (fileName, fileProgress) => {
                          setUploadProgress(prev => ({ ...prev, [fileName]: fileProgress }));
                        }
                      );
                      
                      const filesRes = await caseService.listFiles(data.id);
                      setData((p: any) => ({ ...p, fileGroups: filesRes.data.groups, activeTag: Object.keys(filesRes.data.groups)[0] || null }));
                      const hasPdf = files.some((file: File) => file.name.toLowerCase().endsWith('.pdf') || (file.type && file.type.toLowerCase() === 'application/pdf'));
                      showToast(
                        hasPdf
                          ? `${files.length} files uploaded. We're counting PDF pages and will notify you when done.`
                          : `${files.length} files uploaded successfully`,
                        'success'
                      );
                    } catch (err: any) {
                      showToast(err.message || 'Upload failed', 'error');
                    } finally {
                      setIsUploading(false);
                      setUploadProgress({});
                      if (input) input.value = '';
                    }
                  }}
                />
              </Button>
              ) : null}
              {canUpload ? (
              <Button
                variant="outlined"
                size="small"
                component="label"
                disabled={isUploading}
              >
                {isUploading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <span>Uploading...</span>
                  </Box>
                ) : (
                  'Upload ZIP File'
                )}
                <input
                  type="file"
                  accept=".zip"
                  hidden
                  onChange={async (e) => {
                    if (!data) return;
                    const f = e.target.files?.[0];
                    if (!f) return;
                    
                    const input = e.target;
                    try {
                      setIsUploading(true);
                      setUploadProgress({ [f.name]: 0 });
                      
                      const formData = new FormData();
                      formData.append('file', f);
                      if (allowAdditionalUploads) {
                        formData.append('is_additional_upload', '1');
                      } else if (uploadOptions.isMrrDelivery) {
                        formData.append('is_mrr_delivery', '1');
                      } else if (uploadOptions.isCaseDelivery) {
                        formData.append('is_case_delivery', '1');
                        if (uploadOptions.deliveryComment) {
                          formData.append('delivery_comment', uploadOptions.deliveryComment);
                        }
                      }
                      
                      setUploadProgress({ [f.name]: 0 });
                      // Use the ZIP upload endpoint
                      await caseService.uploadZipFile(
                        data.id,
                        f,
                        {
                          isMrrDelivery: uploadOptions.isMrrDelivery,
                          isCaseDelivery: uploadOptions.isCaseDelivery,
                          deliveryComment: uploadOptions.deliveryComment,
                          isAdditionalUpload: allowAdditionalUploads,
                        },
                        (progress) => setUploadProgress({ [f.name]: progress })
                      );
                      
                      const filesRes = await caseService.listFiles(data.id);
                      setData((p: any) => ({ ...p, fileGroups: filesRes.data.groups, activeTag: Object.keys(filesRes.data.groups)[0] || null }));
                      showToast("ZIP uploaded. We're counting PDF pages and will notify you when done.", 'success');
                    } catch (err: any) {
                      showToast(err.message || 'ZIP upload failed', 'error');
                    } finally {
                      setIsUploading(false);
                      setUploadProgress({});
                      if (input) input.value = '';
                    }
                  }}
                />
              </Button>
              ) : null}
              {/* {!isDelivered || allowAdditionalUploads ? (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setSharePointDialogOpen(true)}
              >
                Import from SharePoint
              </Button>
              ) : null} */}
              <Button
                variant="contained"
                size="small"
                disabled={!((data as any).selectedFileIds || []).length || isDownloading}
                onClick={async () => {
                  if (!data) return;
                  try {
                    if (((data as any).selectedFileIds || []).length === 1) {
                      const fileID = (data as any).selectedFileIds[0];
                      const fileLink = document.getElementById('case_record_' + fileID);
                      if (fileLink) {
                        fileLink.click();
                      }
                    } else {
                      setIsDownloading(true);
                      setDownloadProgress(0);
                      setDownloadingFileName(`case_${data.id}_files.zip`);
                      const blob = await caseService.downloadFilesZip(
                        data.id, 
                        (data as any).selectedFileIds, 
                        `case_${data.id}_files`,
                        (progress) => setDownloadProgress(progress)
                      );
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `case_${data.id}_files.zip`;
                      a.click();
                      URL.revokeObjectURL(url);
                      setIsDownloading(false);
                      setDownloadProgress(0);
                      setDownloadingFileName('');
                    }
                  } catch (e: any) {
                    setIsDownloading(false);
                    setDownloadProgress(0);
                    setDownloadingFileName('');
                    showToast(e.message || 'Download failed', 'error');
                  }
                }}
              >
                Download Selected
              </Button>

              {/* Sync Files - Only visible to pmp admin & site admin */}
              {isPmpAdminOrSiteAdmin() && ((data as any).vendor_assignment || (data as any).client_assignment) && (
                <Button
                  variant="contained"
                  color="secondary"
                  size="small"
                  onClick={async () => {
                    if (!data) return;
                    
                    const isVendorCase = !!(data as any).vendor_assignment;
                    const message = isVendorCase
                      ? 'This will sync all files from this vendor case to the client case. Duplicates will be skipped. Continue?'
                      : 'This will sync all files from the vendor case to this client case. Duplicates will be skipped. Continue?';
                    
                    if (!window.confirm(message)) {
                      return;
                    }
                    
                    try {
                      const response = await caseService.syncFilesToClient(data.id);
                      if (response.success) {
                        showToast(
                          `${response.message} (${response.synced_count} synced, ${response.skipped_count} skipped)`,
                          'success'
                        );
                        
                        // Show detailed info if there were skipped files
                        if (response.skipped_files && response.skipped_files.length > 0) {
                          console.log('Skipped files:', response.skipped_files);
                        }
                        
                        // Refresh files list
                        const filesRes = await caseService.listFiles(data.id);
                        setData((p: any) => ({ ...p, fileGroups: filesRes.data.groups, activeTag: Object.keys(filesRes.data.groups)[0] || null }));
                      }
                    } catch (err: any) {
                      showToast(err.message || 'Failed to sync files', 'error');
                    }
                  }}
                >
                  {(data as any).vendor_assignment ? 'Sync Files to Client Case' : 'Sync Files from Vendor Case'}
                </Button>
              )}
            </Box>
            )}
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={36}><Checkbox size="small" checked={Boolean((data as any).selectedAll)} onChange={(_e, checked) => {
                      const tag = (data as any).activeTag;
                      const rows = tag ? (((data as any).fileGroups?.[tag]) || []) : [];
                      setData((p: any) => ({ ...p, selectedAll: checked, selectedFileIds: checked ? rows.map((r: any) => r.id) : [] }));
                    }} /></TableCell>
                    <TableCell>File Name</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell>Uploaded By</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(() => {
                    const tag = (data as any).activeTag;
                    const rows = tag ? (((data as any).fileGroups?.[tag]) || []) : [];
                    return rows.map((r: any) => (
                      <TableRow key={r.id}>
                        <TableCell><Checkbox size="small" checked={((data as any).selectedFileIds || []).includes(r.id)} onChange={(_e, checked) => {
                          setData((p: any) => {
                            const sel = new Set<number>(p.selectedFileIds || []);
                            if (checked) sel.add(r.id); else sel.delete(r.id);
                            return { ...p, selectedFileIds: Array.from(sel) } as any;
                          });
                        }} /></TableCell>
                        <TableCell>
                          <MLink 
                            component="button"
                            underline="hover"
                            id={"case_record_" + r.id}
                            onClick={async (e: React.MouseEvent) => {
                              e.preventDefault();
                              if (!data) return;
                              try {
                                setIsDownloading(true);
                                setDownloadProgress(0);
                                setDownloadingFileName(r.original_name || r.name);
                                await caseService.downloadFile(
                                  data.id, 
                                  r.id, 
                                  r.original_name || r.name,
                                  (progress) => setDownloadProgress(progress)
                                );
                                setIsDownloading(false);
                                setDownloadProgress(0);
                                setDownloadingFileName('');
                              } catch (err: any) {
                                setIsDownloading(false);
                                setDownloadProgress(0);
                                setDownloadingFileName('');
                                showToast(err.message || 'Download failed', 'error');
                              }
                            }}
                            sx={{ 
                              textAlign: 'left',
                              border: 'none',
                              background: 'none',
                              cursor: 'pointer',
                              padding: 0
                            }}
                          >
                            {r.original_name || r.name}
                          </MLink>
                        </TableCell>
                        <TableCell>{r.size_label}</TableCell>
                        <TableCell>{formatDateTime(r.created_at)}</TableCell>
                        <TableCell>{r.uploaded_by_user?.name || 'Unknown'}</TableCell>
                        <TableCell>
                          {(() => {
                            const isPdf = (r.mime_type === 'application/pdf') || String(r.original_name || r.name || '').toLowerCase().endsWith('.pdf');
                            return (
                              <>
                                {isPdf && isPmpAdmin() && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    sx={{ mr: 0.5 }}
                                    onClick={() => navigate(firmId ? `/firms/${firmId}/cases/${caseId}/document-review/${r.id}` : `/cases/${caseId}/document-review/${r.id}`)}
                                  >
                                    Review
                                  </Button>
                                )}
                                {!hasClientRole() && (
                                  <Button size="small" color="error" onClick={() => {
                                    setFileToDelete({ id: r.id, name: r.original_name || r.name });
                                    setDeleteConfirmOpen(true);
                                  }}>Delete</Button>
                                )}
                              </>
                            );
                          })()}
                        </TableCell>
                      </TableRow>
                    ));
                  })()}
                  {(!((data as any).activeTag) || (((data as any).fileGroups?.[(data as any).activeTag] || []).length === 0)) && (
                    <TableRow>
                      <TableCell colSpan={6} align="center">No files.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Box>
        </AccordionDetails>
      </Accordion>

      {/* Vendor Estimation Section - Show ONLY for Vendor firms (vendor users or PMP admins viewing vendor firm cases) */}
      {data && firm && firm.type === 'vendor' && (isVendorUser() || isSuperAdmin) && (
        <Accordion 
          expanded={expandedAccordions.caseEstimation}
          onChange={() => setExpandedAccordions(prev => ({ ...prev, caseEstimation: !prev.caseEstimation }))}
          sx={{ mb: 2 }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: themeColors.primary,
              '&:hover': { backgroundColor: themeColors.dark },
              color: 'white',
            }}
          >
            <Typography variant="h6" sx={{ color: 'white' }}>Vendor Billing Estimate</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <CaseEstimation
              caseId={data.id}
              projectId={data.project_id}
              user={user}
              isVendorStaff={isVendorUser()}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Client Estimation Section - Show ONLY for Client firms (client firm users or PMP admins viewing client firm cases) */}
      {data && firm && firm.type === 'client' && (isClientFirmUser() || isSuperAdmin) && (
        <Accordion 
          expanded={expandedAccordions.clientEstimation}
          onChange={() => setExpandedAccordions(prev => ({ ...prev, clientEstimation: !prev.clientEstimation }))}
          sx={{ mb: 2 }}
        >
          <AccordionSummary 
            expandIcon={<ExpandMoreIcon />}
            sx={{
              backgroundColor: themeColors.primary,
              '&:hover': { backgroundColor: themeColors.dark },
              color: 'white',
            }}
          >
            <Typography variant="h6" sx={{ color: 'white' }}>Client Billing Estimate</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ClientEstimation
              caseId={data.id}
              projectId={data.project_id}
              user={user}
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* Toast Notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseToast} 
          severity={toast.severity} 
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      {/* Vendor Assignment/Reassignment Dialogs */}
      {/* Initial Assignment Dialog - Reuse the reassignment dialog but without current vendor */}
      <VendorReassignDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        onReassign={handleAssignVendor}
        currentVendor={undefined}
        currentVendorUser={undefined}
      />

      {/* Vendor Reassignment Dialog */}
      <VendorReassignDialog
        open={reassignDialogOpen}
        onClose={() => setReassignDialogOpen(false)}
        onReassign={handleReassignVendor}
        currentVendor={(data as any)?.assigned_vendor || (data as any)?.assignedVendor}
        currentVendorUser={(data as any)?.assigned_vendor_user || (data as any)?.assignedVendorUser}
      />

      <VendorAssignmentHistory
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        history={assignmentHistory}
      />

      {/* Delete File Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => {
          setDeleteConfirmOpen(false);
          setFileToDelete(null);
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the file "{fileToDelete?.name}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setDeleteConfirmOpen(false);
            setFileToDelete(null);
          }}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={async () => {
              if (!data || !fileToDelete) return;
              try {
                await caseService.deleteFile(data.id, fileToDelete.id);
                const filesRes = await caseService.listFiles(data.id);
                setData((p: any) => ({ ...p, fileGroups: filesRes.data.groups, activeTag: Object.keys(filesRes.data.groups)[0] || null }));
                showToast('File deleted successfully', 'success');
                setDeleteConfirmOpen(false);
                setFileToDelete(null);
              } catch (e: any) {
                showToast(e.message || 'Delete failed', 'error');
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* SharePoint Import Dialog */}
      <Dialog open={sharePointDialogOpen} onClose={() => {
        setSharePointDialogOpen(false);
        setSharePointUrl('');
      }} maxWidth="md" fullWidth>
        <DialogTitle>Import Files from SharePoint/OneDrive</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter a publicly accessible SharePoint or OneDrive folder sharing URL. The folder must be shared with "Anyone with the link" permission. All files in the folder will be imported to this case.
          </DialogContentText>
          
          {/* Microsoft Connection Status - HIDDEN FOR NOW */}
          {/* 
          <Box sx={{ mb: 2, p: 2, bgcolor: microsoftConnected ? 'success.light' : 'warning.light', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {microsoftConnected ? '✓ Microsoft Account Connected' : 'Microsoft Account Not Connected'}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                  {microsoftConnected 
                    ? 'You can access files from any SharePoint/OneDrive tenant you have access to.'
                    : 'Connect your Microsoft account to access files from any tenant. Otherwise, the app will use service account (limited to configured tenant).'}
                </Typography>
              </Box>
              {microsoftConnected ? (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={async () => {
                    try {
                      await caseAPI.disconnectMicrosoft();
                      setMicrosoftConnected(false);
                      showToast('Microsoft account disconnected', 'success');
                    } catch (err: any) {
                      showToast(err.message || 'Failed to disconnect', 'error');
                    }
                  }}
                >
                  Disconnect
                </Button>
              ) : (
                <Button
                  size="small"
                  variant="contained"
                  disabled={microsoftConnecting}
                  onClick={async () => {
                    setMicrosoftConnecting(true);
                    try {
                      const res = await caseAPI.getMicrosoftAuthUrl();
                      window.location.href = res.data.data.auth_url;
                    } catch (err: any) {
                      showToast(err.message || 'Failed to get authorization URL', 'error');
                      setMicrosoftConnecting(false);
                    }
                  }}
                >
                  {microsoftConnecting ? 'Connecting...' : 'Connect Microsoft'}
                </Button>
              )}
            </Box>
          </Box>
          */}
          
          <Box sx={{ mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
              Public Sharing Link Required
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              The SharePoint/OneDrive folder must be shared with "Anyone with the link" permission for this to work without authentication.
            </Typography>
          </Box>
          
          <TextField
            autoFocus
            margin="dense"
            label="SharePoint/OneDrive URL"
            placeholder="https://netorgft5464254-my.sharepoint.com/:f:/g/personal/..."
            fullWidth
            variant="outlined"
            value={sharePointUrl}
            onChange={(e) => setSharePointUrl(e.target.value)}
            disabled={sharePointImporting}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={uploadOptions.isMrrDelivery}
                  onChange={(e) => setUploadOptions({ ...uploadOptions, isMrrDelivery: e.target.checked, isCaseDelivery: false })}
                  disabled={sharePointImporting}
                />
              }
              label="MRR Delivery"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={uploadOptions.isCaseDelivery}
                  onChange={(e) => setUploadOptions({ ...uploadOptions, isCaseDelivery: e.target.checked, isMrrDelivery: false })}
                  disabled={sharePointImporting}
                />
              }
              label="Case Delivery"
            />
          </Box>
          {uploadOptions.isCaseDelivery && (
            <TextField
              margin="dense"
              label="Delivery Comment"
              fullWidth
              variant="outlined"
              value={uploadOptions.deliveryComment}
              onChange={(e) => setUploadOptions({ ...uploadOptions, deliveryComment: e.target.value })}
              disabled={sharePointImporting}
              sx={{ mb: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSharePointDialogOpen(false);
              setSharePointUrl('');
            }}
            disabled={sharePointImporting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!data || !sharePointUrl.trim()) {
                showToast('Please enter a valid SharePoint URL', 'error');
                return;
              }
              
              setSharePointImporting(true);
              try {
                const result = await caseService.importFromSharePoint(data.id, sharePointUrl, {
                  isMrrDelivery: uploadOptions.isMrrDelivery,
                  isCaseDelivery: uploadOptions.isCaseDelivery,
                  deliveryComment: uploadOptions.deliveryComment || undefined,
                  isAdditionalUpload: allowAdditionalUploads || isDelivered || isClosed || isCompleted
                });
                
                const filesRes = await caseService.listFiles(data.id);
                setData((p: any) => ({ ...p, fileGroups: filesRes.data.groups, activeTag: Object.keys(filesRes.data.groups)[0] || null }));
                
                if (result.errors && result.errors.length > 0) {
                  showToast(`${result.message} Some files had errors.`, 'warning');
                } else {
                  showToast(result.message || 'Files imported successfully', 'success');
                }
                
                setSharePointDialogOpen(false);
                setSharePointUrl('');
              } catch (err: any) {
                showToast(err.message || 'Failed to import files from SharePoint', 'error');
              } finally {
                setSharePointImporting(false);
              }
            }}
            disabled={sharePointImporting || !sharePointUrl.trim()}
          >
            {sharePointImporting ? 'Importing...' : 'Import Files'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Download Progress Dialog */}
      <Dialog 
        open={isDownloading} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown
        onClose={() => {}} // Prevent closing during download
      >
        <DialogTitle>Downloading File</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              {downloadingFileName}
            </Typography>
            <LinearProgress variant="determinate" value={downloadProgress} />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              {downloadProgress}%
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Upload Progress Dialog */}
      <Dialog 
        open={isUploading && Object.keys(uploadProgress).length > 0} 
        maxWidth="sm" 
        fullWidth
        disableEscapeKeyDown
        onClose={() => {}} // Prevent closing during upload
      >
        <DialogTitle>Uploading Files</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {Object.entries(uploadProgress).map(([fileName, progress]) => (
              <Box key={fileName} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 1 }}>
                    {fileName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {progress}%
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={progress} />
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default CaseView;



